import type { PageType, StarterPageKind } from "../schema/index.js";

export interface TemplateComponentSeed {
  type: string;
  props: Record<string, unknown>;
}

export interface VerticalStarterPage {
  id: string;
  kind: StarterPageKind;
  title: string;
  description: string;
  pageType: PageType;
  aiSummary: string;
  content: TemplateComponentSeed[];
}

export interface VerticalStarterTemplate {
  id: string;
  vertical: string;
  name: string;
  description: string;
  tags: string[];
  pages: VerticalStarterPage[];
}

function summarizeSectionTypes(content: TemplateComponentSeed[]) {
  return content.map((block) => block.type).join(", ");
}

interface SupplementalTemplateConfig {
  aboutTitle: string;
  aboutDescription: string;
  aboutSummary: string;
  aboutBody: string;
  aboutStats?: string;
  bookingTitle: string;
  bookingDescription: string;
  bookingSummary: string;
  bookingBody: string;
  bookingHeading: string;
  bookingCta: string;
  bookingSuccess: string;
  landingTitle: string;
  landingDescription: string;
  landingSummary: string;
  landingHeroTitle: string;
  landingHeroSubtitle: string;
  landingHeroCta: string;
  landingFeatures: string;
  landingFormHeading: string;
  landingFormCta: string;
  landingFormSuccess: string;
}

function buildSupplementalPages(config: SupplementalTemplateConfig): VerticalStarterPage[] {
  return [
    {
      id: "about",
      kind: "about",
      title: "About",
      description: config.aboutDescription,
      pageType: "site_page",
      aiSummary: config.aboutSummary,
      content: [
        { type: "Heading", props: { text: config.aboutTitle, level: "h1", align: "center" } },
        { type: "Text", props: { content: config.aboutBody } },
        ...(config.aboutStats ? [{ type: "StatsCounter", props: { stats: config.aboutStats } }] : []),
      ],
    },
    {
      id: "booking",
      kind: "booking",
      title: "Book",
      description: config.bookingDescription,
      pageType: "booking",
      aiSummary: config.bookingSummary,
      content: [
        { type: "Heading", props: { text: config.bookingTitle, level: "h1", align: "center" } },
        { type: "Text", props: { content: config.bookingBody } },
        {
          type: "ContactForm",
          props: {
            heading: config.bookingHeading,
            submitLabel: config.bookingCta,
            successMessage: config.bookingSuccess,
          },
        },
      ],
    },
    {
      id: "landing",
      kind: "landing",
      title: "Landing",
      description: config.landingDescription,
      pageType: "lead_capture",
      aiSummary: config.landingSummary,
      content: [
        {
          type: "Hero",
          props: {
            title: config.landingHeroTitle,
            subtitle: config.landingHeroSubtitle,
            ctaLabel: config.landingHeroCta,
            ctaUrl: "#lead-form",
            align: "center",
          },
        },
        {
          type: "FeatureGrid",
          props: {
            columns: "3",
            features: config.landingFeatures,
          },
        },
        {
          type: "ContactForm",
          props: {
            heading: config.landingFormHeading,
            submitLabel: config.landingFormCta,
            successMessage: config.landingFormSuccess,
          },
        },
      ],
    },
  ];
}

export const STARTER_TEMPLATES: VerticalStarterTemplate[] = [
  {
    id: "dental-foundation",
    vertical: "dental",
    name: "Bright Smile Dental",
    description: "Trust-first dental starter with treatment overview, patient proof, and a direct appointment path.",
    tags: ["Trust", "Appointments", "Family care"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter focused on reassurance, treatment breadth, and a new-patient CTA.",
        pageType: "site_page",
        aiSummary: "Use a professional dental homepage structure that builds trust fast and moves visitors toward booking.",
        content: [
          {
            type: "Hero",
            props: {
              title: "Your Smile, Our Passion",
              subtitle: "Gentle, modern dentistry for the whole family. New patients welcome — book your first visit today.",
              ctaLabel: "Book Appointment",
              ctaUrl: "/contact",
              align: "center",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "General Dentistry | Cleanings, fillings, and preventive care to keep your smile healthy.\nCosmetic Dentistry | Veneers, whitening, and smile makeovers tailored to you.\nOrthodontics | Invisalign and braces for children and adults.",
            },
          },
          {
            type: "StatsCounter",
            props: {
              stats: "15+ | Years of Experience\n12,000+ | Happy Patients\n98% | Would Recommend Us\n4.9 | Google Rating",
            },
          },
          {
            type: "Testimonials",
            props: {
              quotes:
                "Dr. Patel and her team made my root canal completely painless. I actually fell asleep! | Rachel M. | Patient since 2019\nMy kids used to dread the dentist. Now they ask when their next visit is. | David K. | Father of 3\nThe best dental experience I've ever had. Modern office, warm staff, zero wait time. | Priya S. | New Patient",
            },
          },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Services",
        description: "Treatment menu starter with service categories, pricing anchors, and objection handling.",
        pageType: "site_page",
        aiSummary: "Use a services page that explains treatment options clearly, anchors pricing, and reduces anxiety.",
        content: [
          {
            type: "Heading",
            props: { text: "Dental Services for Every Need", level: "h1", align: "center" },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Teeth Whitening | Professional-grade whitening that's 8 shades brighter in one visit.\nDental Implants | Permanent tooth replacement that looks and feels natural.\nEmergency Care | Same-day appointments for toothaches, chips, and fractures.\nInvisalign | Clear aligners for a straighter smile without metal braces.\nPediatric Dentistry | Fun, gentle care designed specifically for children.\nSleep Dentistry | Sedation options for anxious patients — wake up to a perfect smile.",
            },
          },
          {
            type: "PricingTable",
            props: {
              tier1Name: "Preventive",
              tier1Price: "$99",
              tier1Features: "Comprehensive exam\nDigital X-rays\nProfessional cleaning\nOral cancer screening",
              tier2Name: "Cosmetic",
              tier2Price: "From $299",
              tier2Features: "Teeth whitening\nVeneers consultation\nSmile design preview\nCustom treatment plan",
              tier3Name: "Restorative",
              tier3Price: "From $499",
              tier3Features: "Dental implants\nCrowns & bridges\nRoot canal therapy\nFull-mouth rehab",
            },
          },
          {
            type: "FaqAccordion",
            props: {
              items:
                "Does teeth whitening hurt? | Most patients experience zero discomfort. We use a gentle formula and monitor sensitivity throughout.\nHow long do implants last? | With proper care, dental implants can last a lifetime. They're the gold standard for missing teeth.\nDo you treat children? | Yes! We see patients as young as 1 year old. Our pediatric rooms are designed to make kids feel at ease.",
            },
          },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Appointment-request starter with location info and multiple trust-preserving contact paths.",
        pageType: "site_page",
        aiSummary: "Use a contact page that keeps booking friction low and makes hours, location, and emergency access obvious.",
        content: [
          {
            type: "Heading",
            props: { text: "Book Your Visit", level: "h1", align: "center" },
          },
          {
            type: "Text",
            props: {
              content:
                "Bright Smile Dental is located at 245 Oak Street, Suite 100. We're open Monday through Friday 8am-6pm and Saturday 9am-2pm. Emergency patients are always welcome.",
            },
          },
          {
            type: "ContactForm",
            props: {
              heading: "Request an Appointment",
              submitLabel: "Book Now",
              successMessage: "Thank you! We'll confirm your appointment within 2 hours.",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Phone | Call us at (555) 234-5678 for immediate assistance.\nEmail | hello@brightsmile.dental — we respond within 4 hours.\nEmergency | Same-day emergency line: (555) 234-9999",
            },
          },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "Why Families Trust Bright Smile",
        aboutDescription: "Practice-story starter focused on team credibility and patient experience.",
        aboutSummary: "Use an about page that reduces dental anxiety and explains the practice philosophy in a reassuring way.",
        aboutBody: "Bright Smile Dental was built around one idea: excellent dentistry should feel calm, clear, and deeply human. Our team combines modern clinical standards with gentle communication so patients always understand what comes next and why it matters.",
        aboutStats: "15+ | Years serving local families\n12,000+ | Patients treated\n4.9 | Average rating",
        bookingTitle: "Book Your Appointment",
        bookingDescription: "Booking starter for exams, consultations, and emergency requests.",
        bookingSummary: "Use a booking page that helps patients request the right visit type with minimal friction.",
        bookingBody: "Choose the visit type that best matches your needs, and our front-desk team will confirm timing, intake details, and any insurance questions before you arrive.",
        bookingHeading: "Appointment Request",
        bookingCta: "Request Appointment",
        bookingSuccess: "We received your request and will confirm the appointment shortly.",
        landingTitle: "New Patient Offer",
        landingDescription: "Lead-capture starter for new-patient campaigns and referral traffic.",
        landingSummary: "Use a focused landing page for new-patient specials with one promise and one next step.",
        landingHeroTitle: "A Better First Dental Visit",
        landingHeroSubtitle: "Claim your new-patient consultation and discover a practice that makes every step clearer and calmer.",
        landingHeroCta: "Claim Offer",
        landingFeatures: "Clear First Visit | Know exactly what to expect before you arrive.\nModern Care | Digital diagnostics and gentle treatment planning.\nTrusted Team | Experienced providers and warm support staff.",
        landingFormHeading: "Start as a New Patient",
        landingFormCta: "Get Started",
        landingFormSuccess: "Your request is in. We’ll send next steps shortly.",
      }),
    ],
  },
  {
    id: "yoga-fitness-foundation",
    vertical: "yoga-fitness",
    name: "Zen Flow Yoga & Fitness",
    description: "Membership-focused wellness starter with classes, proof, and a low-friction first-visit path.",
    tags: ["Memberships", "Classes", "Community"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter that sells the atmosphere, class variety, and first-class conversion.",
        pageType: "site_page",
        aiSummary: "Use a wellness homepage that feels welcoming, community-led, and optimized for first-class signups.",
        content: [
          {
            type: "Hero",
            props: {
              title: "Find Your Flow",
              subtitle: "Yoga, pilates, and mindful fitness for every body. First class free — no experience needed.",
              ctaLabel: "Try a Free Class",
              ctaUrl: "/contact",
              align: "center",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Vinyasa Flow | Dynamic sequences that build strength and flexibility in 60 minutes.\nHot Yoga | 95°F room, deep stretches, serious detox. Towel and water included.\nMindful Meditation | 30-minute guided sessions to reduce stress and sharpen focus.",
            },
          },
          {
            type: "StatsCounter",
            props: {
              stats: "40+ | Classes Per Week\n8 | Expert Instructors\n2,500+ | Active Members\n4.8 | Average Rating",
            },
          },
          {
            type: "Testimonials",
            props: {
              quotes:
                "Zen Flow changed my relationship with exercise. I actually look forward to working out now. | Amanda R. | Member for 2 years\nThe instructors remember your name and adjust poses to your level. Truly personal. | Chris T. | Yoga Beginner\nBest studio I've trained at in 10 years. Clean, welcoming, and the community is incredible. | Mika J. | Fitness Enthusiast",
            },
          },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Classes & Memberships",
        description: "Class lineup and plan-comparison starter for studios that sell recurring memberships.",
        pageType: "site_page",
        aiSummary: "Use a services page that explains class formats, removes beginner fear, and gives a strong membership comparison.",
        content: [
          {
            type: "Heading",
            props: { text: "Classes for Every Level", level: "h1", align: "center" },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Beginner Yoga | Slow-paced foundations class. Learn proper alignment and breathing.\nPower Vinyasa | High-energy flow for experienced practitioners. Build heat and strength.\nYin & Restore | Deep stretches held for 3-5 minutes. Perfect for recovery days.\nPilates Core | Mat-based core work that transforms posture and stability.\nBarre Fusion | Ballet-inspired toning with small weights and resistance bands.\nBreathwork & Sound | Weekly sound bath with singing bowls and guided breathwork.",
            },
          },
          {
            type: "PricingTable",
            props: {
              tier1Name: "Drop-In",
              tier1Price: "$22",
              tier1Features: "Single class access\nMat and props included\nShower facilities\nNo commitment",
              tier2Name: "Unlimited",
              tier2Price: "$129/mo",
              tier2Features: "Unlimited classes\nGuest passes (2/mo)\n10% retail discount\nWorkshop priority\nFree mat storage",
              tier3Name: "Annual",
              tier3Price: "$99/mo",
              tier3Features: "Unlimited classes\nGuest passes (4/mo)\n20% retail discount\nAll workshops included\nFree mat storage\nPrivate session (1/quarter)",
            },
          },
          {
            type: "FaqAccordion",
            props: {
              items:
                "I've never done yoga before. Which class should I start with? | Our Beginner Yoga class is designed exactly for you. No flexibility required — just show up.\nWhat should I bring? | Just yourself and water. We provide mats, blocks, straps, and towels for hot yoga.\nCan I freeze my membership? | Yes, you can freeze for up to 30 days per year at no charge.",
            },
          },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Visit-and-message starter with simple lead capture for trials and questions.",
        pageType: "site_page",
        aiSummary: "Use a contact page that makes the first studio visit feel easy and answers parking, schedule, and response-time concerns.",
        content: [
          {
            type: "Heading",
            props: { text: "Visit Us", level: "h1", align: "center" },
          },
          {
            type: "Text",
            props: {
              content:
                "Zen Flow Yoga & Fitness is at 88 River Road, Studio B. We're open 7 days a week: Mon-Fri 6am-9pm, Sat-Sun 7am-6pm. Street parking is free and there's a bike rack out front.",
            },
          },
          {
            type: "ContactForm",
            props: {
              heading: "Send Us a Message",
              submitLabel: "Send",
              successMessage: "Thanks! We'll get back to you within a few hours.",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Phone | (555) 987-6543 — call or text us anytime.\nEmail | hello@zenflow.studio — we reply within 4 hours.\nInstagram | @zenflowstudio — class previews, tips, and community.",
            },
          },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Zen Flow",
        aboutDescription: "Studio-story starter focused on teaching philosophy and community feel.",
        aboutSummary: "Use an about page that makes the studio feel welcoming and explains what makes the teaching approach different.",
        aboutBody: "Zen Flow was created for people who want movement to feel sustainable, not punishing. We blend expert instruction, thoughtful pacing, and community energy so members feel progress without pressure.",
        aboutStats: "40+ | Weekly classes\n8 | Instructors\n2,500+ | Active members",
        bookingTitle: "Book Your First Class",
        bookingDescription: "Booking starter for trial classes and intro consultations.",
        bookingSummary: "Use a booking page that helps first-time visitors choose a class with confidence.",
        bookingBody: "Tell us your experience level, movement goals, and preferred time slots. We’ll guide you toward the right class and make the first visit feel simple.",
        bookingHeading: "Request a Class",
        bookingCta: "Book Class",
        bookingSuccess: "Your request is in. We’ll confirm the best starter class shortly.",
        landingTitle: "Free Class Landing",
        landingDescription: "Lead-capture starter for free-trial campaigns and neighborhood offers.",
        landingSummary: "Use a focused trial-class landing page for paid traffic, referrals, or local campaigns.",
        landingHeroTitle: "Start With a Free Class",
        landingHeroSubtitle: "Try the studio, meet the instructors, and see how easy your first visit can feel.",
        landingHeroCta: "Reserve Free Class",
        landingFeatures: "Beginner Friendly | No prior experience required.\nSupportive Studio | Instructors who guide without intimidation.\nSimple Next Step | One class can turn into a real routine.",
        landingFormHeading: "Reserve Your Spot",
        landingFormCta: "Claim Free Class",
        landingFormSuccess: "You’re on the list. We’ll confirm your class details shortly.",
      }),
    ],
  },
  {
    id: "salon-spa-foundation",
    vertical: "salon-spa",
    name: "Luxe Beauty Salon & Spa",
    description: "Beauty-service starter with premium positioning, service menu clarity, and booking momentum.",
    tags: ["Beauty", "Booking", "Gift cards"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for salons and spas that balances aspiration, proof, and immediate booking.",
        pageType: "site_page",
        aiSummary: "Use a premium beauty homepage that sells results visually and repeats booking intent without feeling pushy.",
        content: [
          {
            type: "Hero",
            props: {
              title: "Where Beauty Meets Relaxation",
              subtitle: "Premium hair, skin, and body treatments in a tranquil downtown setting. Book your escape today.",
              ctaLabel: "Book Now",
              ctaUrl: "/contact",
              align: "center",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Hair Studio | Cuts, color, balayage, and keratin treatments by award-winning stylists.\nSpa Treatments | Facials, body wraps, and aromatherapy massage for total renewal.\nNail Bar | Gel, acrylics, nail art, and luxe manicure-pedicure combos.",
            },
          },
          {
            type: "StatsCounter",
            props: {
              stats: "8 | Years in Business\n15,000+ | Clients Served\n4.9 | Google Rating\n22 | Skilled Stylists",
            },
          },
          {
            type: "Testimonials",
            props: {
              quotes:
                "Best balayage I've ever had. Natasha understood exactly what I wanted from a single photo. | Olivia P. | Regular Client\nThe hot stone massage was heavenly. I fell asleep and woke up feeling 10 years younger. | Mark D. | First-Time Visitor\nI've tried every salon in town. Luxe is the only one I keep coming back to. | Jessica L. | Client since 2020",
            },
          },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Services & Pricing",
        description: "Service-menu starter with packaged pricing and new-client incentives.",
        pageType: "site_page",
        aiSummary: "Use a beauty services page that makes menu browsing easy, price anchors clear, and first-time booking compelling.",
        content: [
          {
            type: "Heading",
            props: { text: "Our Service Menu", level: "h1", align: "center" },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Women's Haircut & Style | Consultation, precision cut, blowout, and styling. From $65.\nBalayage & Highlights | Hand-painted color for a natural, sun-kissed look. From $150.\nSignature Facial | 60-min deep cleanse, exfoliation, mask, and massage. $95.\nSwedish Massage | 60 or 90 minutes of full-body relaxation. From $85.\nGel Manicure | Long-lasting gel polish with cuticle care. $45.\nBridal Package | Hair trial, day-of styling, makeup, and touch-up kit. From $350.",
            },
          },
          {
            type: "PricingTable",
            props: {
              tier1Name: "Express",
              tier1Price: "From $45",
              tier1Features: "Blowout or gel mani\n30-minute service\nWalk-in friendly\nComplement with add-ons",
              tier2Name: "Signature",
              tier2Price: "From $95",
              tier2Features: "Cut + color or facial\n60-90 minute service\nComplimentary beverage\nParaben-free products",
              tier3Name: "VIP Day",
              tier3Price: "From $299",
              tier3Features: "Full glam package\nHair + skin + nails\nPrivate treatment room\nChampagne & snacks\nTake-home product kit",
            },
          },
          {
            type: "FaqAccordion",
            props: {
              items:
                "How far in advance should I book? | We recommend 1-2 weeks for cuts and 2-3 weeks for color services. Same-day availability varies.\nDo you use cruelty-free products? | Yes. All our hair, skin, and nail products are cruelty-free and paraben-free.\nIs there parking? | Free parking is available in the lot behind our building. Street parking is also available.",
            },
          },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Booking-request starter with location, hours, and channel variety.",
        pageType: "site_page",
        aiSummary: "Use a contact page that supports direct appointments, quick questions, and social proof of responsiveness.",
        content: [
          {
            type: "Heading",
            props: { text: "Book Your Appointment", level: "h1", align: "center" },
          },
          {
            type: "Text",
            props: {
              content:
                "Luxe Beauty Salon & Spa is at 142 Main Street, Downtown. Open Tuesday-Saturday 9am-7pm, Sunday 10am-4pm. Closed Mondays.",
            },
          },
          {
            type: "ContactForm",
            props: {
              heading: "Request a Booking",
              submitLabel: "Request Appointment",
              successMessage: "We'll confirm your appointment within 1 hour.",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Phone | (555) 321-7890 — call to book or ask questions.\nEmail | bookings@luxebeauty.com — we respond same day.\nInstagram | @luxebeautyspa — our latest work and promotions.",
            },
          },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Luxe Beauty",
        aboutDescription: "Brand-story starter focused on service philosophy and beauty standards.",
        aboutSummary: "Use an about page that explains the salon experience and reinforces premium positioning without sounding generic.",
        aboutBody: "Luxe Beauty Salon & Spa exists to make beauty services feel indulgent, polished, and personal. We combine technical expertise with hospitality standards that make every appointment feel like time well spent.",
        aboutStats: "22 | Stylists and therapists\n15,000+ | Clients served\n4.9 | Average rating",
        bookingTitle: "Request an Appointment",
        bookingDescription: "Booking starter for hair, skin, and spa services.",
        bookingSummary: "Use a booking page that helps clients request the right service and timing in one flow.",
        bookingBody: "Share the service you want, your preferred stylist or therapist, and the times that work best for you. Our team will confirm the ideal slot quickly.",
        bookingHeading: "Beauty Booking Request",
        bookingCta: "Book Appointment",
        bookingSuccess: "We received your request and will confirm your appointment shortly.",
        landingTitle: "New Client Offer",
        landingDescription: "Lead-capture starter for first-visit campaigns and premium seasonal offers.",
        landingSummary: "Use a focused salon landing page for a single first-visit offer with one conversion path.",
        landingHeroTitle: "Your First Luxe Visit Starts Here",
        landingHeroSubtitle: "Claim a new-client beauty offer and experience the salon with zero guesswork.",
        landingHeroCta: "Unlock Offer",
        landingFeatures: "Premium Results | Expert-led hair, skin, and nail services.\nEasy First Visit | Clear pricing and fast appointment confirmation.\nRelaxed Experience | Hospitality-driven service from arrival to checkout.",
        landingFormHeading: "Claim Your First Visit",
        landingFormCta: "Get My Offer",
        landingFormSuccess: "Your offer request is in. We’ll send appointment options next.",
      }),
    ],
  },
  {
    id: "coaching-foundation",
    vertical: "coaching",
    name: "Peak Performance Coaching",
    description: "Authority-led coaching starter with proof, program packaging, and consultation conversion.",
    tags: ["Authority", "Lead gen", "Programs"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for coaches who sell through credibility, outcomes, and a consult CTA.",
        pageType: "site_page",
        aiSummary: "Use a coaching homepage that positions the coach as credible, outcome-focused, and easy to contact for a strategy call.",
        content: [
          {
            type: "Hero",
            props: {
              title: "Scale Your Business Without Burning Out",
              subtitle: "Executive coaching for founders and leaders who want to grow revenue, build better teams, and reclaim their time.",
              ctaLabel: "Book a Strategy Call",
              ctaUrl: "/contact",
              align: "center",
            },
          },
          {
            type: "StatsCounter",
            props: {
              stats: "200+ | Leaders Coached\n$47M | Revenue Generated by Clients\n92% | Client Retention Rate\n15 | Years of Experience",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "1:1 Executive Coaching | Deep-dive sessions focused on your biggest bottleneck. Bi-weekly for 6 months.\nGroup Mastermind | Join 8 ambitious founders in a structured peer advisory group. Monthly sessions.\nTeam Workshops | Half-day workshops on leadership, communication, and strategic planning for your team.",
            },
          },
          {
            type: "Testimonials",
            props: {
              quotes:
                "Working with Marcus doubled our revenue in 14 months. More importantly, I stopped working weekends. | Jennifer Liu | CEO, TechBridge Solutions\nThe mastermind group gave me the accountability I was missing. Best investment I've made in my business. | Robert Hayes | Founder, Hayes & Co.\nMarcus helped me fire myself from operations and focus on strategy. Our profit margin jumped 22%. | Diana Park | Managing Director, Apex Ventures",
            },
          },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Programs",
        description: "Offer-packaging starter for coaching retainers, mastermind offers, and intensive sessions.",
        pageType: "site_page",
        aiSummary: "Use a programs page that packages offers clearly, differentiates outcomes, and handles objections for premium services.",
        content: [
          {
            type: "Heading",
            props: { text: "Coaching Programs", level: "h1", align: "center" },
          },
          {
            type: "PricingTable",
            props: {
              tier1Name: "Accelerator",
              tier1Price: "$2,500/mo",
              tier1Features: "Bi-weekly 60-min sessions\nVoxer access between calls\nCustom growth roadmap\nQuarterly business review\n6-month commitment",
              tier2Name: "Mastermind",
              tier2Price: "$1,500/mo",
              tier2Features: "Monthly group session (8 founders)\nHot seat coaching rotation\nPrivate Slack community\nGuest expert sessions\nAnnual retreat included",
              tier3Name: "VIP Day",
              tier3Price: "$5,000",
              tier3Features: "Full day 1:1 intensive\n90-day action plan\nTeam assessment included\nFollow-up call (30 days)\nAll materials and frameworks",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "2",
              features:
                "Strategy & Growth | Revenue modeling, pricing strategy, market positioning, and growth levers.\nLeadership & Team | Hiring frameworks, delegation systems, culture design, and conflict resolution.\nOperations & Systems | Process optimization, KPI dashboards, and removing yourself as the bottleneck.\nMindset & Performance | Energy management, decision-making frameworks, and founder resilience.",
            },
          },
          {
            type: "FaqAccordion",
            props: {
              items:
                "How is this different from other coaching? | I've built and sold businesses myself. I don't teach theory — I share what actually worked and help you apply it to your specific situation.\nWhat results can I expect? | Past clients have doubled revenue, reduced work hours by 30%, and successfully exited. Results depend on your commitment.\nDo you offer a guarantee? | If after 90 days you haven't seen measurable progress, I'll extend your program at no charge.",
            },
          },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Consultation-request starter with qualification framing and response expectations.",
        pageType: "site_page",
        aiSummary: "Use a contact page that positions the consultation as valuable while keeping the application flow simple.",
        content: [
          {
            type: "Heading",
            props: { text: "Let's Talk About Your Growth", level: "h1", align: "center" },
          },
          {
            type: "Text",
            props: {
              content:
                "The first step is a free 30-minute strategy call. No sales pitch — just a genuine conversation about where you are, where you want to be, and whether coaching is the right path.",
            },
          },
          {
            type: "ContactForm",
            props: {
              heading: "Apply for a Strategy Call",
              submitLabel: "Submit Application",
              successMessage: "Application received. Marcus will personally review it within 48 hours.",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features:
                "Email | marcus@peakperformance.coach\nLinkedIn | /in/marcuscole — connect and follow for daily insights.\nPodcast | 'The Growth Lever' — weekly episodes on Spotify and Apple Podcasts.",
            },
          },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Peak Performance Coaching",
        aboutDescription: "Founder-story starter focused on methodology, credibility, and client outcomes.",
        aboutSummary: "Use an about page that explains why this coach is credible and how the coaching model actually works.",
        aboutBody: "Peak Performance Coaching helps founders and leaders create sustainable growth, not just busier calendars. The work blends strategic clarity, leadership systems, and decision-making discipline so results are measurable and repeatable.",
        aboutStats: "200+ | Leaders coached\n$47M | Client revenue impact\n92% | Retention rate",
        bookingTitle: "Book a Strategy Call",
        bookingDescription: "Booking starter for consultations and fit calls.",
        bookingSummary: "Use a booking page that qualifies fit while still making the first call feel valuable.",
        bookingBody: "Use this first call to clarify your growth bottleneck, define the right next move, and see whether the coaching relationship is the right match.",
        bookingHeading: "Strategy Call Request",
        bookingCta: "Book Call",
        bookingSuccess: "Your request is in. We’ll send available call times shortly.",
        landingTitle: "Growth Intensive Landing",
        landingDescription: "Lead-capture starter for a single coaching offer or founder intensive.",
        landingSummary: "Use a focused coaching landing page for one premium offer with a single high-intent CTA.",
        landingHeroTitle: "Fix the Bottleneck Slowing Your Growth",
        landingHeroSubtitle: "Apply for a strategy intensive designed to reveal the constraint and map the next 90 days.",
        landingHeroCta: "Apply Now",
        landingFeatures: "Sharp Diagnosis | Identify the true constraint, not just the loudest problem.\nClear Plan | Leave with practical next actions and priorities.\nExperienced Guide | Advice grounded in real operating experience.",
        landingFormHeading: "Apply for the Intensive",
        landingFormCta: "Submit Application",
        landingFormSuccess: "Application received. We’ll review fit and reply shortly.",
      }),
    ],
  },
  {
    id: "legal-foundation",
    vertical: "legal",
    name: "Carter & Associates Law",
    description: "Professional law-firm starter with practice areas, attorney credibility, and consultation conversion.",
    tags: ["Consultations", "Trust", "Business law"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for law firms balancing credibility, proof, and free-consultation conversion.",
        pageType: "site_page",
        aiSummary: "Use a legal homepage that builds trust fast, explains practice coverage, and moves visitors toward a consultation request.",
        content: [
          {
            type: "Hero",
            props: {
              title: "Trusted Legal Counsel When It Matters Most",
              subtitle: "Business law, real estate, and estate planning. Serving families and businesses for over 20 years.",
              ctaLabel: "Free Consultation",
              ctaUrl: "/contact",
              align: "center",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Business Law | Entity formation, contracts, employment law, and M&A for growing companies.\nReal Estate | Residential and commercial transactions, zoning disputes, and landlord-tenant matters.\nEstate Planning | Wills, trusts, powers of attorney, and probate administration.",
            },
          },
          {
            type: "StatsCounter",
            props: {
              stats: "20+ | Years in Practice\n3,500+ | Cases Handled\n98% | Client Satisfaction\n4 | Experienced Attorneys",
            },
          },
          {
            type: "Testimonials",
            props: {
              quotes: "Sarah Carter saved our business during a partnership dispute. Her strategic thinking and calm demeanor were exactly what we needed. | Richard N. | Business Owner\nThey handled our home purchase flawlessly. Every document was explained clearly and the closing was seamless. | The Morales Family | First-Time Homebuyers\nAfter my father passed, the probate process felt overwhelming. This firm guided us through every step with compassion. | Linda T. | Estate Client",
            },
          },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Practice Areas",
        description: "Practice-area starter with service scope, pricing posture, and objection handling.",
        pageType: "site_page",
        aiSummary: "Use a services page that explains legal offerings clearly, sets fee expectations, and reduces consultation friction.",
        content: [
          {
            type: "Heading",
            props: { text: "Our Practice Areas", level: "h1", align: "center" },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Business Formation | LLC, S-Corp, C-Corp setup. Operating agreements, bylaws, and compliance.\nContracts & Agreements | Drafting, review, and negotiation of commercial contracts and NDAs.\nEmployment Law | Handbook creation, compliance audits, and dispute resolution.\nReal Estate Transactions | Purchase agreements, title review, closings, and lease negotiations.\nEstate Planning | Wills, revocable trusts, healthcare directives, and powers of attorney.\nProbate & Trust Admin | Estate settlement, asset distribution, and fiduciary guidance.",
            },
          },
          {
            type: "PricingTable",
            props: {
              tier1Name: "Consultation",
              tier1Price: "Free",
              tier1Features: "30-minute meeting\nCase evaluation\nStrategy overview\nNo obligation\nIn-person or video",
              tier2Name: "Flat-Fee Services",
              tier2Price: "From $750",
              tier2Features: "LLC formation from $750\nSimple will from $500\nContract review from $350\nNo surprise bills\nClear scope upfront",
              tier3Name: "Ongoing Counsel",
              tier3Price: "From $1,500/mo",
              tier3Features: "Dedicated attorney\nUnlimited consultations\nContract reviews included\nCompliance monitoring\nPriority response (24h)",
            },
          },
          {
            type: "FaqAccordion",
            props: {
              items: "Do you offer free consultations? | Yes. Every new client receives a free 30-minute consultation to discuss their situation and explore options.\nHow are your fees structured? | We offer flat fees for standard services and hourly billing for complex litigation. We always provide a written fee agreement before work begins.\nDo you handle cases outside your practice areas? | We have a trusted referral network. If your matter falls outside our expertise, we'll connect you with the right attorney.",
            },
          },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Consultation-request starter with office info and low-friction contact paths.",
        pageType: "site_page",
        aiSummary: "Use a contact page that makes consultation booking easy and keeps logistics, hours, and contact methods obvious.",
        content: [
          {
            type: "Heading",
            props: { text: "Schedule Your Consultation", level: "h1", align: "center" },
          },
          {
            type: "Text",
            props: {
              content: "Carter & Associates Law is located at 500 Commerce Boulevard, Suite 400. Office hours: Monday-Friday 8:30am-5:30pm. Evening and weekend appointments available by request.",
            },
          },
          {
            type: "ContactForm",
            props: {
              heading: "Request a Consultation",
              submitLabel: "Submit Request",
              successMessage: "Thank you. A member of our team will contact you within one business day.",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Phone | (555) 567-8900 — speak with our intake coordinator.\nEmail | info@carterlaw.com — confidential inquiries welcome.\nFax | (555) 567-8901 — for document submissions.",
            },
          },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Carter & Associates",
        aboutDescription: "Firm-story starter focused on trust, professionalism, and legal approach.",
        aboutSummary: "Use an about page that builds legal trust through tone, credentials, and practical client philosophy.",
        aboutBody: "Carter & Associates combines legal precision with direct communication. Clients come to us when the details matter, and stay because they always know what we are doing, why it matters, and what comes next.",
        aboutStats: "20+ | Years in practice\n4 | Attorneys\n3,500+ | Matters handled",
        bookingTitle: "Request a Consultation",
        bookingDescription: "Booking starter for legal consultation requests.",
        bookingSummary: "Use a booking page that makes legal intake feel orderly, calm, and professional.",
        bookingBody: "Share the practice area, urgency, and a short outline of your situation. Our intake team will route the request to the right attorney and confirm the next step.",
        bookingHeading: "Consultation Intake",
        bookingCta: "Request Consultation",
        bookingSuccess: "Your request is in. Our team will follow up within one business day.",
        landingTitle: "Consultation Landing",
        landingDescription: "Lead-capture starter for one practice-area campaign or firm offer.",
        landingSummary: "Use a focused legal landing page with one practice-area message and one consultation action.",
        landingHeroTitle: "Start With a Clear Legal Conversation",
        landingHeroSubtitle: "Book a consultation with a firm that values preparation, direct advice, and practical outcomes.",
        landingHeroCta: "Book Consultation",
        landingFeatures: "Qualified Guidance | Speak with a team that handles this category regularly.\nClear Next Steps | Understand fees, likely path, and immediate priorities.\nProfessional Intake | Confidential, structured, and responsive from the start.",
        landingFormHeading: "Start Your Consultation",
        landingFormCta: "Request Review",
        landingFormSuccess: "Thank you. We’ll review your request and respond shortly.",
      }),
    ],
  },
  {
    id: "home-services-foundation",
    vertical: "home-services",
    name: "TrustLine Plumbing & HVAC",
    description: "Trust-focused home-services starter with emergency dispatch, transparent pricing, and review-driven conversion.",
    tags: ["Emergency service", "Trust", "Transparent pricing"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for plumbers and HVAC companies centered on urgency, trust, and same-day booking.",
        pageType: "site_page",
        aiSummary: "Use a home-services homepage that signals speed, trust, and no-surprise pricing while pushing emergency or same-day calls to action.",
        content: [
          {
            type: "Hero",
            props: {
              title: "Plumbing & HVAC You Can Trust",
              subtitle: "Licensed, insured, and 5-star rated. Same-day emergency service. No surprise bills — ever.",
              ctaLabel: "Call Now: (555) 789-0123",
              ctaUrl: "tel:5557890123",
              align: "center",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Plumbing Repair | Leaks, clogs, burst pipes, and water heater repair. Available 24/7 for emergencies.\nHVAC Service | Installation, repair, and maintenance for central air, heat pumps, and furnaces.\nDrain Cleaning | Professional hydro-jetting and camera inspection to clear tough blockages.",
            },
          },
          {
            type: "StatsCounter",
            props: {
              stats: "15,000+ | Homes Serviced\n4.8 | Google Rating (800+ reviews)\n60 min | Average Response Time\n100% | Satisfaction Guarantee",
            },
          },
          {
            type: "Testimonials",
            props: {
              quotes: "Our furnace died at 11pm on a Friday. TrustLine had a tech here in 45 minutes and had it running by midnight. | Karen W. | Emergency Call\nHonest pricing, clean work, and they even put booties on before entering our house. Old-school professionalism. | Tom & Lisa R. | Repeat Customers\nThey found the issue two other plumbers missed. Fixed it right the first time and the price was exactly what they quoted. | Angela M. | Homeowner",
            },
          },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Services & Pricing",
        description: "Service menu starter for home-services teams with dispatch pricing and maintenance-plan upsell.",
        pageType: "site_page",
        aiSummary: "Use a services page that explains repair and maintenance offers clearly and reinforces upfront pricing and emergency availability.",
        content: [
          {
            type: "Heading",
            props: { text: "Our Services", level: "h1", align: "center" },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Water Heater | Tank and tankless installation, repair, and annual flushing. Same-day replacement available.\nAC Installation | Central air, mini-splits, and heat pumps. Free in-home estimate with load calculation.\nDrain & Sewer | Hydro-jetting, camera inspection, and trenchless sewer repair. No-dig solutions available.\nFurnace Repair | Diagnostics, ignitor replacement, heat exchanger inspection. All brands serviced.\nBathroom Remodel | Complete plumbing rough-in and fixture installation for renovations.\nMaintenance Plans | Annual tune-ups for HVAC and plumbing. Prevents breakdowns and extends equipment life.",
            },
          },
          {
            type: "PricingTable",
            props: {
              tier1Name: "Service Call",
              tier1Price: "$79",
              tier1Features: "Diagnostic fee\nApplied to repair cost\nUpfront pricing before work\nNo hidden fees\nSame-day available",
              tier2Name: "Maintenance Plan",
              tier2Price: "$19/mo",
              tier2Features: "Annual HVAC tune-up\nAnnual plumbing check\n15% off all repairs\nPriority scheduling\nNo overtime charges",
              tier3Name: "Emergency",
              tier3Price: "$99 dispatch",
              tier3Features: "24/7 availability\n60-min response target\nNo night/weekend surcharge\nUpfront pricing always\nFull warranty on repairs",
            },
          },
          {
            type: "FaqAccordion",
            props: {
              items: "Do you charge extra for nights and weekends? | No. Our emergency rate is a flat $99 dispatch fee, regardless of when you call. The repair is quoted upfront before we start.\nAre your technicians licensed? | Yes. Every technician is state-licensed, insured, background-checked, and drug-tested.\nDo you offer financing? | Yes. We offer 0% financing for 12 months on installations over $1,000 through GreenSky.",
            },
          },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Dispatch-request starter with emergency and office lines clearly separated.",
        pageType: "site_page",
        aiSummary: "Use a contact page that keeps service booking fast and makes emergency versus non-urgent channels obvious.",
        content: [
          {
            type: "Heading",
            props: { text: "Request Service", level: "h1", align: "center" },
          },
          {
            type: "Text",
            props: {
              content: "TrustLine Plumbing & HVAC serves the entire metro area. Office hours: Mon-Fri 7am-7pm, Sat 8am-4pm. Emergency service available 24/7/365.",
            },
          },
          {
            type: "ContactForm",
            props: {
              heading: "Schedule Service",
              submitLabel: "Request Appointment",
              successMessage: "We'll call you within 30 minutes to confirm your appointment window.",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Emergency Line | (555) 789-0123 — 24/7, answered by a real person.\nOffice | (555) 789-0100 — scheduling, billing, and general questions.\nEmail | service@trustlinehvac.com — for non-urgent inquiries.",
            },
          },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About TrustLine",
        aboutDescription: "Company-story starter focused on service standards and local trust.",
        aboutSummary: "Use an about page that reinforces reliability, technician quality, and homeowner trust.",
        aboutBody: "TrustLine Plumbing & HVAC was built around one promise: homeowners should never have to choose between fast help and honest service. Our technicians show up prepared, explain the repair clearly, and stand behind the work.",
        aboutStats: "15,000+ | Homes serviced\n60 min | Average emergency response\n4.8 | Review rating",
        bookingTitle: "Schedule Service",
        bookingDescription: "Booking starter for non-emergency and same-day service requests.",
        bookingSummary: "Use a booking page that separates emergencies from standard requests while keeping the flow fast.",
        bookingBody: "Tell us the service type, urgency, and the best time window. We’ll match the request to the right technician and confirm the visit quickly.",
        bookingHeading: "Service Request",
        bookingCta: "Book Service",
        bookingSuccess: "Request received. We’ll call shortly to confirm your appointment window.",
        landingTitle: "Same-Day Service Landing",
        landingDescription: "Lead-capture starter for one urgent home-service offer.",
        landingSummary: "Use a focused home-services landing page for emergency or same-day campaigns with one response path.",
        landingHeroTitle: "Need Same-Day Help?",
        landingHeroSubtitle: "Get a licensed technician fast, with upfront pricing and real-time confirmation.",
        landingHeroCta: "Request Service",
        landingFeatures: "Fast Dispatch | Quick routing to the right technician.\nUpfront Pricing | No hidden fees after arrival.\nTrusted Service | Licensed, insured, and reviewed by local homeowners.",
        landingFormHeading: "Request Same-Day Help",
        landingFormCta: "Send Request",
        landingFormSuccess: "We have your request and will respond shortly.",
      }),
    ],
  },
  {
    id: "restaurant-foundation",
    vertical: "restaurant",
    name: "Harvest Kitchen & Bar",
    description: "Restaurant starter with menu highlights, reservations, and event-led storytelling.",
    tags: ["Reservations", "Seasonal menu", "Private dining"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for restaurants that sell atmosphere, reviews, and reservation urgency.",
        pageType: "site_page",
        aiSummary: "Use a restaurant homepage that makes the venue feel desirable, highlights social proof, and drives table reservations quickly.",
        content: [
          {
            type: "Hero",
            props: {
              title: "Farm to Table, Heart to Plate",
              subtitle: "Seasonal American cuisine crafted from locally sourced ingredients. Reservations now open for dinner.",
              ctaLabel: "Reserve a Table",
              ctaUrl: "/contact",
              align: "center",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Seasonal Menu | Our menu changes quarterly to showcase the freshest local produce and proteins.\nCraft Cocktails | Handcrafted drinks made with house-infused spirits and seasonal garnishes.\nPrivate Dining | Our Garden Room seats 30 guests for celebrations, corporate events, and rehearsal dinners.",
            },
          },
          {
            type: "StatsCounter",
            props: {
              stats: "12 | Local Farm Partners\n4.7 | Yelp Rating\n850+ | 5-Star Reviews\n2018 | Established",
            },
          },
          {
            type: "Testimonials",
            props: {
              quotes: "The tasting menu was one of the best dining experiences of my life. Every course was a revelation. | Sarah & Tom | Anniversary Dinner\nWe host all our client dinners here. The private room and service are impeccable. | James W. | Business Owner\nFinally, a restaurant that takes allergies seriously without compromising on flavor. | Nina K. | Food Blogger",
            },
          },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Menu",
        description: "Menu starter with category highlights, tasting-menu anchor, and dietary objection handling.",
        pageType: "site_page",
        aiSummary: "Use a menu page that highlights key offers clearly, anchors premium dining options, and makes booking the next step obvious.",
        content: [
          {
            type: "Heading",
            props: { text: "Spring 2026 Menu", level: "h1", align: "center" },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "2",
              features: "Starters | Burrata with heirloom tomatoes ($16) / Grilled octopus with chimichurri ($19) / Roasted beet salad with goat cheese ($14)\nMains | Pan-seared salmon with asparagus ($32) / Grass-fed ribeye with truffle fries ($42) / Wild mushroom risotto ($26) / Roasted half chicken with herb jus ($28)\nDesserts | Lemon olive oil cake ($12) / Dark chocolate torte ($14) / Seasonal fruit crumble ($11)\nDrinks | Craft cocktails ($15-18) / Wine by the glass ($12-22) / Local craft beer ($8-10) / Non-alcoholic pairings ($8)",
            },
          },
          {
            type: "PricingTable",
            props: {
              tier1Name: "Lunch",
              tier1Price: "$22-38",
              tier1Features: "2-course express menu\nServed Tue-Fri 11:30-2pm\nGlass of house wine $8\nFree parking",
              tier2Name: "Dinner",
              tier2Price: "$32-55",
              tier2Features: "Full seasonal menu\nServed Tue-Sat 5:30-10pm\nSomm wine pairings\nReservations recommended",
              tier3Name: "Tasting Menu",
              tier3Price: "$95/person",
              tier3Features: "7-course journey\nOptional wine pairing +$55\nChef's selection\nAvailable Thu-Sat\nAdvance booking required",
            },
          },
          {
            type: "FaqAccordion",
            props: {
              items: "Do you accommodate dietary restrictions? | Absolutely. We offer vegetarian, vegan, and gluten-free options. Please mention allergies when booking.\nIs there a dress code? | Smart casual. We want you to feel comfortable while enjoying an elevated experience.\nCan I book the private dining room? | Yes. The Garden Room seats up to 30 guests. Contact us for custom menus and pricing.",
            },
          },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Reservations",
        description: "Reservation-request starter with hours, events, and same-day contact options.",
        pageType: "site_page",
        aiSummary: "Use a reservations page that makes hours and contact methods obvious and lowers friction for same-day table requests.",
        content: [
          {
            type: "Heading",
            props: { text: "Reserve Your Table", level: "h1", align: "center" },
          },
          {
            type: "Text",
            props: {
              content: "Harvest Kitchen & Bar is at 320 Vine Street, Downtown. Lunch: Tue-Fri 11:30am-2pm. Dinner: Tue-Sat 5:30pm-10pm. Sunday Brunch: 10am-2pm. Closed Mondays.",
            },
          },
          {
            type: "ContactForm",
            props: {
              heading: "Request a Reservation",
              submitLabel: "Request Table",
              successMessage: "We'll confirm your reservation within 30 minutes.",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Phone | (555) 876-5432 — call for same-day availability.\nEmail | reservations@harvestkitchen.com\nPrivate Events | events@harvestkitchen.com for parties of 10+",
            },
          },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Harvest Kitchen",
        aboutDescription: "Restaurant-story starter focused on sourcing, hospitality, and atmosphere.",
        aboutSummary: "Use an about page that explains the restaurant philosophy and makes the experience feel intentional.",
        aboutBody: "Harvest Kitchen & Bar is built around seasonal ingredients, warm hospitality, and a dining room that feels both elevated and relaxed. We work closely with local producers so the menu always reflects the best of the moment.",
        aboutStats: "12 | Farm partners\n850+ | Five-star reviews\n2018 | Opened",
        bookingTitle: "Reserve a Table",
        bookingDescription: "Booking starter for table requests and event dining.",
        bookingSummary: "Use a booking page that makes reservation requests and special-occasion notes simple.",
        bookingBody: "Choose your preferred date, party size, and any dining notes. We’ll confirm the best available seating and timing as quickly as possible.",
        bookingHeading: "Reservation Request",
        bookingCta: "Reserve Table",
        bookingSuccess: "Reservation request received. We’ll confirm availability shortly.",
        landingTitle: "Dining Offer Landing",
        landingDescription: "Lead-capture starter for one dining promotion or event campaign.",
        landingSummary: "Use a focused restaurant landing page for brunch, tasting menus, or seasonal events with one reservation CTA.",
        landingHeroTitle: "Book the Season’s Most Talked-About Table",
        landingHeroSubtitle: "Reserve your place for a limited menu, tasting experience, or special event night.",
        landingHeroCta: "Reserve Now",
        landingFeatures: "Seasonal Menu | Distinctive dishes built around fresh ingredients.\nAtmosphere | A dining room designed for memorable nights out.\nSimple Booking | Quick confirmation and clear event details.",
        landingFormHeading: "Reserve This Experience",
        landingFormCta: "Claim Table",
        landingFormSuccess: "Your request is in. We’ll confirm the reservation shortly.",
      }),
    ],
  },
  {
    id: "real-estate-foundation",
    vertical: "real-estate",
    name: "Compass Point Realty",
    description: "Real-estate starter with market proof, team credibility, and lead-capture flows for buyers and sellers.",
    tags: ["Listings", "Valuation", "Market proof"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for brokerages centered on trust, local proof, and search or valuation conversion.",
        pageType: "site_page",
        aiSummary: "Use a real-estate homepage that combines local market proof with strong buyer and seller calls to action.",
        content: [
          {
            type: "Hero",
            props: {
              title: "Find Your Next Home With Confidence",
              subtitle: "Local expertise, market intelligence, and personalized service. Buying or selling — we make it seamless.",
              ctaLabel: "Search Listings",
              ctaUrl: "/services",
              align: "center",
            },
          },
          {
            type: "StatsCounter",
            props: {
              stats: "$285M | Sold in 2025\n420+ | Homes Sold\n18 | Days Avg. Time on Market\n12 | Licensed Agents",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Buyers | From first-timers to investors, we guide you through every step — pre-approval to closing.\nSellers | Strategic pricing, professional staging, and marketing that gets your home sold faster and for more.\nInvestors | Off-market deals, rental analysis, and portfolio strategy for residential and commercial properties.",
            },
          },
          {
            type: "Testimonials",
            props: {
              quotes: "We were first-time buyers in a competitive market. Our agent found us the perfect home and negotiated $15K below asking. | The Patels | Homeowners\nThey sold our house in 9 days, $22K over asking price. The staging advice and marketing were phenomenal. | Robert & Janet M. | Sellers\nCompass Point manages my 6-unit portfolio. Tenant screening, maintenance, everything handled professionally. | Carlos D. | Investor",
            },
          },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Services",
        description: "Buyer, seller, and property-management offer starter with pricing posture and FAQs.",
        pageType: "site_page",
        aiSummary: "Use a services page that makes buyer and seller help concrete, explains commission structure, and reduces market confusion.",
        content: [
          {
            type: "Heading",
            props: { text: "How We Help You", level: "h1", align: "center" },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Buyer Representation | Full-service buyer's agent from search to close. We negotiate hard so you don't have to.\nListing & Selling | Professional photography, 3D tours, targeted digital ads, and open house coordination.\nProperty Management | Tenant placement, rent collection, maintenance coordination, and monthly reporting.\nMarket Analysis | Detailed comparable sales data and pricing strategy — free for prospective clients.\nRelocation Services | Area tours, school district guidance, and coordination with out-of-state agents.\nInvestment Advisory | ROI analysis, 1031 exchange guidance, and off-market deal sourcing.",
            },
          },
          {
            type: "PricingTable",
            props: {
              tier1Name: "Buyers",
              tier1Price: "No Upfront Cost",
              tier1Features: "Full buyer representation\nNegotiation advocacy\nInspection coordination\nClosing support\nCommission paid by seller",
              tier2Name: "Sellers",
              tier2Price: "5% Commission",
              tier2Features: "Professional photography\n3D virtual tour\nMLS + digital marketing\nOpen houses\nStaging consultation\nNegotiation to close",
              tier3Name: "Property Mgmt",
              tier3Price: "8% of Rent",
              tier3Features: "Tenant screening\nRent collection\nMaintenance coordination\nMonthly statements\n24/7 emergency line\nLease management",
            },
          },
          {
            type: "FaqAccordion",
            props: {
              items: "How much do I need for a down payment? | It depends on the loan type. FHA loans require as little as 3.5%. We'll connect you with lenders who can pre-approve you.\nHow long does it take to sell a home? | Our average is 18 days on market, compared to the area average of 34 days. Proper pricing and marketing make the difference.\nDo you handle commercial properties? | Yes. We have agents specializing in retail, office, and multi-family commercial transactions.",
            },
          },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Agent-contact and lead-capture starter for valuation requests and buyer inquiries.",
        pageType: "site_page",
        aiSummary: "Use a contact page that makes agent outreach and valuation requests easy while keeping response expectations explicit.",
        content: [
          {
            type: "Heading",
            props: { text: "Get in Touch", level: "h1", align: "center" },
          },
          {
            type: "Text",
            props: {
              content: "Compass Point Realty is at 780 Market Street, Suite 200. Office hours: Mon-Fri 9am-6pm, Sat 10am-4pm. Open houses every Sunday — check our listings page for details.",
            },
          },
          {
            type: "ContactForm",
            props: {
              heading: "Contact Us",
              submitLabel: "Send Message",
              successMessage: "An agent will respond within 2 hours during business hours.",
            },
          },
          {
            type: "FeatureGrid",
            props: {
              columns: "3",
              features: "Phone | (555) 234-7800 — call to speak with an agent.\nEmail | hello@compasspointrealty.com\nText | (555) 234-7801 — text us for quick answers.",
            },
          },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Compass Point Realty",
        aboutDescription: "Brokerage-story starter focused on local expertise and service philosophy.",
        aboutSummary: "Use an about page that reinforces local market knowledge and client trust without generic real-estate clichés.",
        aboutBody: "Compass Point Realty helps buyers, sellers, and investors make smarter property decisions with better information and closer guidance. We combine neighborhood knowledge, clear communication, and disciplined execution from first call to closing table.",
        aboutStats: "$285M | Sold in 2025\n420+ | Homes sold\n12 | Licensed agents",
        bookingTitle: "Book a Property Consultation",
        bookingDescription: "Booking starter for buyer, seller, or valuation consultations.",
        bookingSummary: "Use a booking page that helps prospects choose the right real-estate conversation quickly.",
        bookingBody: "Share whether you’re buying, selling, relocating, or investing, and we’ll pair you with the right advisor and next step.",
        bookingHeading: "Consultation Request",
        bookingCta: "Book Consultation",
        bookingSuccess: "Your request is in. An agent will follow up shortly.",
        landingTitle: "Home Valuation Landing",
        landingDescription: "Lead-capture starter for seller campaigns and valuation offers.",
        landingSummary: "Use a focused real-estate landing page for one seller or buyer offer with one lead path.",
        landingHeroTitle: "See What Your Home Could Sell For",
        landingHeroSubtitle: "Get a local valuation backed by real market activity, not just an automated estimate.",
        landingHeroCta: "Request Valuation",
        landingFeatures: "Local Data | Market insight from active neighborhood specialists.\nSeller Strategy | Learn what drives pricing and time-to-sale.\nFast Response | Clear next steps without pressure.",
        landingFormHeading: "Request a Valuation",
        landingFormCta: "Get My Estimate",
        landingFormSuccess: "Thanks. We’ll prepare the valuation details and follow up shortly.",
      }),
    ],
  },
  {
    id: "medical-foundation",
    vertical: "medical",
    name: "Northwell Family Clinic",
    description: "Trust-led medical starter for clinics that need clear care pathways, proof, and low-friction appointment requests.",
    tags: ["Appointments", "Trust", "Primary care"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter that establishes safety, clinical breadth, and fast next steps.",
        pageType: "site_page",
        aiSummary: "Use a clinic homepage that feels calm, credible, and easy to act on for first-time patients.",
        content: [
          { type: "Hero", props: { title: "Care That Puts Families First", subtitle: "Primary care, chronic care, and same-week appointments with a team that listens.", ctaLabel: "Book a Visit", ctaUrl: "/booking", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Family Medicine | Preventive care, annual exams, and long-term health plans.\nWomen's Health | Screenings, wellness visits, and personalized care pathways.\nSame-Day Visits | Fast help for urgent but non-emergency concerns." } },
          { type: "Testimonials", props: { quotes: "The first clinic that made me feel heard from the first minute. | Elena M. | Patient\nFast scheduling, kind staff, and no confusion about next steps. | Victor T. | New patient" } },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Services",
        description: "Care-path starter with service clusters and FAQ-led reassurance.",
        pageType: "site_page",
        aiSummary: "Use a services page that explains care categories simply and reduces anxiety about booking the wrong appointment.",
        content: [
          { type: "Heading", props: { text: "Clinical Services", level: "h1", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Preventive Care | Annual checkups, screenings, vaccines, and wellness planning.\nChronic Care | Diabetes, hypertension, thyroid, and medication management.\nLab & Diagnostics | On-site bloodwork coordination and referral-ready test review.\nPediatrics | School forms, growth tracking, and routine child visits.\nTelehealth | Follow-ups and medication reviews from home.\nTravel Medicine | Vaccines, prep consultations, and destination guidance." } },
          { type: "FaqAccordion", props: { items: "Do you accept new patients? | Yes, we reserve new-patient slots every week.\nCan I book online? | Yes, choose a service and request a time through our booking page.\nWhat if I need a specialist? | We coordinate referrals and follow-up notes so nothing gets lost." } },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Clinic contact starter with location, hours, and intake expectations.",
        pageType: "site_page",
        aiSummary: "Use a contact page that removes uncertainty around location, hours, response time, and urgent care escalation.",
        content: [
          { type: "Heading", props: { text: "Contact the Clinic", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Northwell Family Clinic is at 120 Cedar Avenue. Office hours: Mon-Fri 8am-6pm, Sat 9am-1pm. For medical emergencies, call emergency services immediately." } },
          { type: "ContactForm", props: { heading: "Send a Question", submitLabel: "Contact Us", successMessage: "Thanks. Our care team will reply during business hours." } },
        ],
      },
      {
        id: "about",
        kind: "about",
        title: "About",
        description: "Provider-story starter that builds trust without over-claiming.",
        pageType: "site_page",
        aiSummary: "Use an about page that introduces the care philosophy, provider approach, and community context in a human way.",
        content: [
          { type: "Heading", props: { text: "About Our Practice", level: "h1", align: "center" } },
          { type: "Text", props: { content: "We built Northwell Family Clinic around continuity of care: fewer handoffs, more clarity, and plans patients can actually follow. Our providers focus on prevention, early intervention, and relationships that last." } },
          { type: "StatsCounter", props: { stats: "18+ | Years serving families\n6 | Clinical providers\n4.9 | Average patient rating" } },
        ],
      },
      {
        id: "booking",
        kind: "booking",
        title: "Book a Visit",
        description: "Appointment-request starter that makes service choice and expectations explicit.",
        pageType: "booking",
        aiSummary: "Use a booking page that helps patients pick the right appointment type and know what happens next.",
        content: [
          { type: "Heading", props: { text: "Request an Appointment", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Choose the visit type that matches your needs. If you are unsure, request a general consultation and our staff will route you correctly." } },
          { type: "ContactForm", props: { heading: "Appointment Request", submitLabel: "Request Appointment", successMessage: "We received your request and will confirm your slot shortly." } },
        ],
      },
      {
        id: "landing",
        kind: "landing",
        title: "New Patient Landing",
        description: "Conversion page starter for new-patient campaigns and referral traffic.",
        pageType: "lead_capture",
        aiSummary: "Use a focused landing page for first-visit offers or referral traffic with one clear promise and one action.",
        content: [
          { type: "Hero", props: { title: "New Patient Visits Made Simple", subtitle: "Schedule your first appointment with a clinic that values clarity, speed, and compassionate care.", ctaLabel: "Claim Your First Visit", ctaUrl: "#lead-form", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Fast Intake | Minimal paperwork and clear next steps.\nCare Coordination | Referrals and follow-up support handled for you.\nTrusted Team | Experienced providers and responsive support staff." } },
          { type: "ContactForm", props: { heading: "Start Here", submitLabel: "Get Started", successMessage: "We’ll help you choose the right appointment and confirm availability." } },
        ],
      },
    ],
  },
  {
    id: "ecommerce-foundation",
    vertical: "ecommerce",
    name: "Northstar Goods",
    description: "Product-led ecommerce starter with category discovery, conversion pages, and campaign-ready landing flows.",
    tags: ["Products", "Offers", "Conversion"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter that balances category discovery, offer framing, and proof.",
        pageType: "site_page",
        aiSummary: "Use an ecommerce homepage that gets to bestsellers quickly and keeps one primary conversion path visible.",
        content: [
          { type: "Hero", props: { title: "Everyday Essentials, Better Made", subtitle: "Shop curated home, office, and lifestyle goods designed to last longer and look cleaner.", ctaLabel: "Shop Bestsellers", ctaUrl: "/services", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Bestsellers | Proven favorites customers come back for.\nBundles | Smart sets that increase value without decision fatigue.\nFast Fulfillment | Clear shipping windows and responsive support." } },
          { type: "StatsCounter", props: { stats: "15k+ | Orders shipped\n4.8 | Average rating\n32% | Repeat customers" } },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Collections",
        description: "Collection and offer starter that groups products into buying intent.",
        pageType: "site_page",
        aiSummary: "Use a product collections page that organizes inventory by customer intent instead of internal category jargon.",
        content: [
          { type: "Heading", props: { text: "Shop by Collection", level: "h1", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Workspace | Desk tools, lighting, organizers, and focus-friendly setups.\nTravel | Compact carry solutions and durable everyday pieces.\nHome Refresh | Elevated basics that make daily routines feel intentional.\nStarter Kits | Best-value bundles for first-time customers.\nGifting | Fast-pick sets with built-in presentation value.\nSeasonal Drops | Limited launches and small-batch collaborations." } },
          { type: "PricingTable", props: { tier1Name: "Single Items", tier1Price: "From $24", tier1Features: "Individual product purchase\nStandard shipping\n30-day returns", tier2Name: "Curated Bundles", tier2Price: "From $79", tier2Features: "Higher average value\nBundle-only pricing\nGift-ready packaging", tier3Name: "Subscriptions", tier3Price: "From $29/mo", tier3Features: "Recurring essentials\nPriority fulfillment\nMember-only drops" } },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Support starter for shoppers who need order, shipping, or product help.",
        pageType: "site_page",
        aiSummary: "Use a support page that keeps order questions, returns, and product guidance easy to resolve.",
        content: [
          { type: "Heading", props: { text: "Customer Support", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Need help with an order, a return, or a product question? Our support team responds Monday through Saturday within a few business hours." } },
          { type: "ContactForm", props: { heading: "Send a Support Request", submitLabel: "Contact Support", successMessage: "Your message is in. We’ll reply as soon as possible." } },
        ],
      },
      {
        id: "about",
        kind: "about",
        title: "About",
        description: "Brand-story starter focused on product standards and buying philosophy.",
        pageType: "site_page",
        aiSummary: "Use an about page that explains why the catalog exists, what quality means, and why customers should trust the curation.",
        content: [
          { type: "Heading", props: { text: "Why Northstar Goods Exists", level: "h1", align: "center" } },
          { type: "Text", props: { content: "We started Northstar Goods to reduce decision fatigue. Fewer products, better standards, cleaner design, and customer service that actually answers." } },
          { type: "Testimonials", props: { quotes: "The site feels curated instead of overwhelming. I know everything here was chosen on purpose. | Jamie L.\nGreat packaging, quick shipping, and product quality that matches the photos. | Matt S." } },
        ],
      },
      {
        id: "booking",
        kind: "booking",
        title: "Personal Shopping",
        description: "Consultation-style starter for styling, gifting, or B2B procurement requests.",
        pageType: "booking",
        aiSummary: "Use a booking page for higher-touch shopping help, bulk orders, or gifting consults.",
        content: [
          { type: "Heading", props: { text: "Book a Personal Shopping Session", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Need gifting help, workspace recommendations, or a curated order for your team? Request a short consultation and we’ll build the right shortlist." } },
          { type: "ContactForm", props: { heading: "Request a Session", submitLabel: "Book Consultation", successMessage: "We’ll confirm your consultation shortly." } },
        ],
      },
      {
        id: "landing",
        kind: "landing",
        title: "Offer Landing",
        description: "Campaign-ready product offer page with one conversion action.",
        pageType: "lead_capture",
        aiSummary: "Use a focused ecommerce landing page for a seasonal offer, bestseller bundle, or paid campaign traffic.",
        content: [
          { type: "Hero", props: { title: "Upgrade Your Setup in One Order", subtitle: "Get our most popular productivity bundle with launch pricing and fast fulfillment.", ctaLabel: "Unlock the Offer", ctaUrl: "#lead-form", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Bundle Value | Better than buying separately.\nFast Delivery | Clear shipping expectations.\nLow Risk | Hassle-free returns and human support." } },
          { type: "ContactForm", props: { heading: "Get the Offer", submitLabel: "Claim Bundle", successMessage: "Offer unlocked. We’ll send the next steps right away." } },
        ],
      },
    ],
  },
  {
    id: "education-foundation",
    vertical: "education",
    name: "Summit Learning Studio",
    description: "Education starter for programs, enrollment, and parent/student trust-building.",
    tags: ["Programs", "Enrollment", "Trust"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter that sells outcomes, teaching approach, and enrollment momentum.",
        pageType: "site_page",
        aiSummary: "Use an education homepage that makes the learning promise concrete and easy to trust for parents or adult learners.",
        content: [
          { type: "Hero", props: { title: "Learning That Builds Confidence", subtitle: "Small-group programs, expert instructors, and a clear path from first inquiry to real progress.", ctaLabel: "Explore Programs", ctaUrl: "/services", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Core Academics | Math, reading, and science support with measurable milestones.\nExam Prep | Structured preparation plans for high-stakes assessments.\nConfidence Coaching | Habits, study systems, and motivation support." } },
          { type: "Testimonials", props: { quotes: "My daughter’s confidence changed within weeks, not months. | Parent\nThe structure finally made studying feel manageable again. | Adult learner" } },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Programs",
        description: "Program lineup starter for curriculum, delivery modes, and enrollment logic.",
        pageType: "site_page",
        aiSummary: "Use a programs page that helps families or learners self-select without confusion.",
        content: [
          { type: "Heading", props: { text: "Programs for Every Stage", level: "h1", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Primary Support | Foundational literacy and numeracy with weekly progress notes.\nSecondary Coaching | Subject tutoring plus planning and study routines.\nCollege Prep | Essay support, interview prep, and admissions planning.\nAdult Upskilling | Business writing, communication, and exam preparation.\nSmall Groups | Community accountability with expert facilitation.\nPrivate Sessions | One-on-one plans for focused progress." } },
          { type: "FaqAccordion", props: { items: "Do you offer online sessions? | Yes, both online and in-person options are available.\nHow do you place students? | We start with a short assessment and recommendation call.\nCan parents track progress? | Yes, every program includes milestone reporting." } },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Enrollment-contact starter with response expectations and next steps.",
        pageType: "site_page",
        aiSummary: "Use a contact page that makes the inquiry-to-enrollment path feel guided and low-risk.",
        content: [
          { type: "Heading", props: { text: "Talk to Our Team", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Tell us about the student, learner goals, and preferred schedule. We’ll recommend the best next step and match you to the right program." } },
          { type: "ContactForm", props: { heading: "Ask About Enrollment", submitLabel: "Send Inquiry", successMessage: "Thanks. An advisor will reach out shortly." } },
        ],
      },
      {
        id: "about",
        kind: "about",
        title: "About",
        description: "Team-and-method starter that explains teaching philosophy.",
        pageType: "site_page",
        aiSummary: "Use an about page that explains how your learning model works and why it gets results.",
        content: [
          { type: "Heading", props: { text: "Our Teaching Philosophy", level: "h1", align: "center" } },
          { type: "Text", props: { content: "We combine structure, encouragement, and visible progress markers. Students stay motivated because they always know what they’re improving and what comes next." } },
          { type: "StatsCounter", props: { stats: "92% | Goal completion rate\n14 | Specialist educators\n1,800+ | Learners supported" } },
        ],
      },
      {
        id: "booking",
        kind: "booking",
        title: "Book an Assessment",
        description: "Assessment-booking starter for consultations and placement calls.",
        pageType: "booking",
        aiSummary: "Use a booking page for assessments or placement calls with a clear value exchange.",
        content: [
          { type: "Heading", props: { text: "Book a Learning Assessment", level: "h1", align: "center" } },
          { type: "Text", props: { content: "A short assessment helps us recommend the right program, pacing, and instructor match before enrollment." } },
          { type: "ContactForm", props: { heading: "Request Assessment", submitLabel: "Book Assessment", successMessage: "We’ll confirm your assessment time shortly." } },
        ],
      },
      {
        id: "landing",
        kind: "landing",
        title: "Enrollment Landing",
        description: "Lead-capture starter for a cohort, bootcamp, or seasonal enrollment push.",
        pageType: "lead_capture",
        aiSummary: "Use a campaign landing page for one cohort or one core offer with a single action path.",
        content: [
          { type: "Hero", props: { title: "Join the Next Learning Cohort", subtitle: "Structured support, expert teaching, and a clear plan for measurable progress.", ctaLabel: "Reserve Your Spot", ctaUrl: "#lead-form", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Clear Outcomes | Transparent goals and milestones.\nExpert Support | Specialists who know the curriculum deeply.\nParent Visibility | Progress feedback built into the program." } },
          { type: "ContactForm", props: { heading: "Reserve a Spot", submitLabel: "Apply Now", successMessage: "Your interest is in. We’ll send enrollment details next." } },
        ],
      },
    ],
  },
  {
    id: "photography-foundation",
    vertical: "photography",
    name: "Northlight Photography",
    description: "Portfolio-first starter for photographers who need trust, packages, and booking momentum.",
    tags: ["Portfolio", "Packages", "Booking"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for personal-brand trust and visual proof.",
        pageType: "site_page",
        aiSummary: "Use a photography homepage that leads with taste, trust, and a confident next step.",
        content: [
          { type: "Hero", props: { title: "Images With Feeling, Not Just Coverage", subtitle: "Wedding, brand, and family photography with calm direction and timeless editing.", ctaLabel: "View Packages", ctaUrl: "/services", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Weddings | Full-day storytelling with natural direction and emotional coverage.\nBrand Sessions | Editorial-style imagery for founders, teams, and campaigns.\nFamily Portraits | Relaxed sessions that feel easy even if cameras normally feel awkward." } },
          { type: "Testimonials", props: { quotes: "The photos felt like us, not a staged version of us. | Wedding client\nThe session was calm, clear, and so much easier than I expected. | Brand client" } },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Packages",
        description: "Offer-and-package starter that clarifies scope and value.",
        pageType: "site_page",
        aiSummary: "Use a services page that makes package differences clear and reduces back-and-forth before inquiry.",
        content: [
          { type: "Heading", props: { text: "Photography Packages", level: "h1", align: "center" } },
          { type: "PricingTable", props: { tier1Name: "Portrait Session", tier1Price: "$450", tier1Features: "60-minute session\nOnline gallery\n40 edited images\nLocation guidance", tier2Name: "Brand Session", tier2Price: "$980", tier2Features: "2-hour session\nCreative direction\nMultiple looks\nCommercial usage guidance", tier3Name: "Wedding Coverage", tier3Price: "From $2,800", tier3Features: "Timeline planning\n8+ hours coverage\nPreview gallery in 72h\nFull story delivery" } },
          { type: "FaqAccordion", props: { items: "Do you help with posing? | Yes, I guide naturally the whole way through.\nHow fast is delivery? | Preview images arrive within 72 hours and full galleries follow on the agreed timeline.\nDo you travel? | Yes, travel is available for selected locations and destination work." } },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Inquiry starter for dates, project fit, and response-time clarity.",
        pageType: "site_page",
        aiSummary: "Use a contact page that makes it easy to ask about availability, packages, and project fit.",
        content: [
          { type: "Heading", props: { text: "Start Your Inquiry", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Share your date, session type, and what matters most to you. I reply personally with availability, package fit, and next steps." } },
          { type: "ContactForm", props: { heading: "Tell Me About Your Project", submitLabel: "Send Inquiry", successMessage: "Thank you. I’ll reply with availability and options soon." } },
        ],
      },
      {
        id: "about",
        kind: "about",
        title: "About",
        description: "Personal-story starter for creative voice and working style.",
        pageType: "site_page",
        aiSummary: "Use an about page that explains how you work, what you notice, and what clients can expect from the experience.",
        content: [
          { type: "Heading", props: { text: "Behind the Camera", level: "h1", align: "center" } },
          { type: "Text", props: { content: "I photograph people at their best when they forget they’re being photographed. My process is calm, gently directed, and built around trust more than performance." } },
          { type: "StatsCounter", props: { stats: "250+ | Sessions photographed\n9 | Years behind the camera\n72h | Typical preview turnaround" } },
        ],
      },
      {
        id: "booking",
        kind: "booking",
        title: "Book a Consult",
        description: "Consultation-booking starter for discovery calls and date checks.",
        pageType: "booking",
        aiSummary: "Use a booking page for consult calls that qualifies fit before the full quote process.",
        content: [
          { type: "Heading", props: { text: "Book a Discovery Call", level: "h1", align: "center" } },
          { type: "Text", props: { content: "A short call helps us confirm fit, talk through the vision, and choose the right package before booking." } },
          { type: "ContactForm", props: { heading: "Request a Call", submitLabel: "Book Call", successMessage: "Great. I’ll send available times shortly." } },
        ],
      },
      {
        id: "landing",
        kind: "landing",
        title: "Mini Session Landing",
        description: "Campaign starter for seasonal mini sessions or special offers.",
        pageType: "lead_capture",
        aiSummary: "Use a focused landing page for limited-time mini sessions with one offer and one response path.",
        content: [
          { type: "Hero", props: { title: "Seasonal Mini Sessions Are Open", subtitle: "A short, beautifully paced session with a clear package and limited availability.", ctaLabel: "Reserve a Time", ctaUrl: "#lead-form", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Fast Sessions | Perfect for families and quick brand refreshes.\nCurated Locations | Clean backgrounds and easy access.\nSimple Delivery | A polished gallery without a long wait." } },
          { type: "ContactForm", props: { heading: "Reserve a Spot", submitLabel: "Claim Session", successMessage: "Your request is in. I’ll confirm the remaining availability." } },
        ],
      },
    ],
  },
  {
    id: "fitness-foundation",
    vertical: "fitness",
    name: "Forge Performance Club",
    description: "Gym and training starter for memberships, programs, and performance-led enrollment.",
    tags: ["Memberships", "Training", "Performance"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for gyms that sell energy, coaching, and immediate sign-up momentum.",
        pageType: "site_page",
        aiSummary: "Use a fitness homepage that feels high-energy, coach-led, and clear about the first step.",
        content: [
          { type: "Hero", props: { title: "Train With Purpose", subtitle: "Strength, conditioning, and expert coaching for people who want measurable progress.", ctaLabel: "Start Your Trial", ctaUrl: "/booking", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Strength Training | Structured programming with expert oversight.\nSmall Group Coaching | Energy, accountability, and individual adjustments.\nPerformance Testing | Track progress with metrics that actually matter." } },
          { type: "StatsCounter", props: { stats: "1,200+ | Active members\n45+ | Classes per week\n4.9 | Average rating" } },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Programs",
        description: "Offer lineup starter for classes, memberships, and coaching tiers.",
        pageType: "site_page",
        aiSummary: "Use a services page that helps prospects pick the right training path without confusion.",
        content: [
          { type: "Heading", props: { text: "Programs and Memberships", level: "h1", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Open Gym | Flexible access for self-directed training.\nCoach-Led Classes | Daily sessions built for strength and conditioning.\n1:1 Coaching | Personalized programming and accountability.\nFoundations | Beginner onboarding for safe, confident starts.\nAthlete Prep | Sport-specific strength and power development.\nRecovery Support | Mobility, programming adjustments, and deload planning." } },
          { type: "PricingTable", props: { tier1Name: "Open Gym", tier1Price: "$79/mo", tier1Features: "Gym access\nEquipment orientation\nWeekly programming board", tier2Name: "Unlimited", tier2Price: "$149/mo", tier2Features: "All classes\nOpen gym included\nCoach support", tier3Name: "Elite", tier3Price: "$249/mo", tier3Features: "Unlimited training\n1:1 sessions monthly\nPerformance review" } },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Lead-capture starter for training questions and membership inquiries.",
        pageType: "site_page",
        aiSummary: "Use a contact page that makes trial and membership questions easy to resolve quickly.",
        content: [
          { type: "Heading", props: { text: "Talk to the Team", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Tell us your training goals, experience level, and preferred times. We’ll recommend the best first step and membership fit." } },
          { type: "ContactForm", props: { heading: "Ask About Membership", submitLabel: "Send Inquiry", successMessage: "Thanks. A coach will reach out shortly." } },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Forge Performance",
        aboutDescription: "Gym-story starter focused on coaching standards and culture.",
        aboutSummary: "Use an about page that explains the gym philosophy and why the coaching model works.",
        aboutBody: "Forge Performance Club is built for people who want more than access to equipment. We combine coaching, structure, and progress tracking so members know what they are doing, why they are doing it, and how they are improving.",
        aboutStats: "12 | Coaches\n45+ | Weekly classes\n1,200+ | Members",
        bookingTitle: "Book a Trial Session",
        bookingDescription: "Booking starter for trial classes and consults.",
        bookingSummary: "Use a booking page that helps prospects start with confidence and the right session type.",
        bookingBody: "Choose a trial, intro consult, or coaching assessment, and we’ll match you to the right starting point.",
        bookingHeading: "Trial Request",
        bookingCta: "Book Trial",
        bookingSuccess: "Your request is in. We’ll send trial details shortly.",
        landingTitle: "Trial Offer Landing",
        landingDescription: "Lead-capture starter for one gym offer or challenge launch.",
        landingSummary: "Use a focused fitness landing page for one challenge or one introductory offer.",
        landingHeroTitle: "Start Strong With a Guided Trial",
        landingHeroSubtitle: "Join a coach-led intro session and see how fast the right training environment changes consistency.",
        landingHeroCta: "Claim Trial",
        landingFeatures: "Coach Guidance | Support from the first session.\nClear Structure | Know exactly where to start.\nReal Progress | Build momentum, not confusion.",
        landingFormHeading: "Reserve Your Trial",
        landingFormCta: "Start Now",
        landingFormSuccess: "You’re in. We’ll send your next steps soon.",
      }),
    ],
  },
  {
    id: "health-foundation",
    vertical: "health",
    name: "Northlight Wellness Center",
    description: "Holistic health starter for wellness programs, consultations, and trust-led care journeys.",
    tags: ["Wellness", "Programs", "Consultations"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for wellness brands that balance calm authority and action.",
        pageType: "site_page",
        aiSummary: "Use a wellness homepage that feels grounded, trustworthy, and easy to begin with.",
        content: [
          { type: "Hero", props: { title: "Whole-Person Wellness, Practically Applied", subtitle: "Personalized wellness programs, expert practitioners, and support you can actually sustain.", ctaLabel: "Explore Programs", ctaUrl: "/services", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Wellness Plans | Personalized programs built around real goals.\nPractitioner Support | Experienced guidance with clear next steps.\nSustainable Habits | Focus on progress that lasts beyond the first month." } },
          { type: "Testimonials", props: { quotes: "The first wellness plan that felt realistic instead of overwhelming. | Client\nI finally had a clear path, not just scattered advice. | Program member" } },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Programs",
        description: "Program lineup starter for coaching, recovery, and consultation-led offers.",
        pageType: "site_page",
        aiSummary: "Use a services page that groups wellness offers into clear program tracks and lowers decision friction.",
        content: [
          { type: "Heading", props: { text: "Wellness Programs", level: "h1", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Energy & Recovery | Restore energy, sleep quality, and stress resilience.\nNutrition Coaching | Habit-based support built for real life.\nMovement Support | Gentle, progressive routines that match capacity.\nHormone Wellness | Consultation-led care plans and practitioner guidance.\nStress Reset | Practical programs for nervous-system support.\nExecutive Wellness | High-accountability plans for busy professionals." } },
          { type: "FaqAccordion", props: { items: "Do I need lab work first? | Not always. We’ll help determine what is useful based on your goals.\nAre programs personalized? | Yes, every plan is adjusted to your context and constraints.\nCan I work with you remotely? | Yes, many programs are available virtually." } },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Inquiry starter for wellness questions and practitioner matching.",
        pageType: "site_page",
        aiSummary: "Use a contact page that helps prospects ask questions and get matched to the right program or practitioner.",
        content: [
          { type: "Heading", props: { text: "Start the Conversation", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Share your goals, current challenges, and preferred support style. We’ll recommend the best next step and practitioner match." } },
          { type: "ContactForm", props: { heading: "Ask a Question", submitLabel: "Send Request", successMessage: "Thanks. Our team will follow up shortly." } },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Northlight Wellness",
        aboutDescription: "Practice-story starter focused on care philosophy and sustainable change.",
        aboutSummary: "Use an about page that explains the wellness philosophy clearly and builds practical trust.",
        aboutBody: "Northlight Wellness Center exists to turn overwhelming health goals into clear, sustainable plans. We focus on practical care, calm guidance, and measurable progress instead of hype or complexity.",
        aboutStats: "9 | Practitioners\n1,600+ | Clients supported\n4.9 | Average rating",
        bookingTitle: "Book a Wellness Consult",
        bookingDescription: "Booking starter for consultations and discovery calls.",
        bookingSummary: "Use a booking page that helps prospects take the first step without confusion.",
        bookingBody: "Book a short consult to clarify your goals, understand the right starting point, and decide which program or practitioner fit is best.",
        bookingHeading: "Consult Request",
        bookingCta: "Book Consult",
        bookingSuccess: "Your request is in. We’ll confirm your consultation shortly.",
        landingTitle: "Wellness Reset Landing",
        landingDescription: "Lead-capture starter for one wellness offer or program launch.",
        landingSummary: "Use a focused health landing page for a single program or reset offer with one clear CTA.",
        landingHeroTitle: "Reset Your Health With a Clear Plan",
        landingHeroSubtitle: "Start with a structured wellness consult and a program that matches your real life.",
        landingHeroCta: "Begin Reset",
        landingFeatures: "Clear Guidance | Know exactly what to do next.\nPersonal Fit | Get matched to the right support path.\nSustainable Change | Build progress that lasts.",
        landingFormHeading: "Start Your Reset",
        landingFormCta: "Apply Now",
        landingFormSuccess: "Thanks. We’ll send your next steps shortly.",
      }),
    ],
  },
  {
    id: "agency-foundation",
    vertical: "agency",
    name: "Northline Creative Agency",
    description: "Agency starter for strategy-led services, portfolio proof, and discovery-call conversion.",
    tags: ["Strategy", "Creative", "Discovery calls"],
    pages: [
      {
        id: "home",
        kind: "home",
        title: "Home",
        description: "Homepage starter for agencies that need credibility, clarity, and pipeline momentum.",
        pageType: "site_page",
        aiSummary: "Use an agency homepage that explains what the agency does, why it works, and how prospects engage next.",
        content: [
          { type: "Hero", props: { title: "Creative That Moves the Business Forward", subtitle: "Strategy, brand systems, and conversion-ready execution for teams that want better outcomes, not more noise.", ctaLabel: "Book Discovery Call", ctaUrl: "/booking", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Brand Strategy | Positioning, messaging, and identity systems.\nCampaign Execution | Launch-ready pages, creative, and growth assets.\nDesign Systems | Reusable UI and marketing systems that scale with the team." } },
          { type: "StatsCounter", props: { stats: "85+ | Client launches\n3.4x | Avg. lift on key campaigns\n92% | Repeat engagement rate" } },
        ],
      },
      {
        id: "services",
        kind: "services",
        title: "Services",
        description: "Agency offer lineup starter for strategy, design, and execution services.",
        pageType: "site_page",
        aiSummary: "Use a services page that packages agency work clearly and reduces ambiguity around scope.",
        content: [
          { type: "Heading", props: { text: "What We Do", level: "h1", align: "center" } },
          { type: "FeatureGrid", props: { columns: "3", features: "Brand Systems | Positioning, visual identity, and rollout guidance.\nGrowth Pages | Landing pages, funnel pages, and conversion assets.\nCreative Direction | Campaign concepts and execution frameworks.\nRetainers | Ongoing design and optimization support.\nLaunch Sprints | Focused 2-4 week execution for urgent priorities.\nInternal Systems | Design operations and reusable marketing systems." } },
          { type: "PricingTable", props: { tier1Name: "Sprint", tier1Price: "From $4,500", tier1Features: "Short, focused execution\nClear scope\nFast launch path", tier2Name: "Project", tier2Price: "From $12,000", tier2Features: "Strategy + execution\nCross-functional delivery\nReview checkpoints", tier3Name: "Retainer", tier3Price: "From $6,000/mo", tier3Features: "Ongoing support\nPriority backlog\nContinuous optimization" } },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        title: "Contact",
        description: "Lead-capture starter for discovery calls and project inquiries.",
        pageType: "site_page",
        aiSummary: "Use a contact page that makes project-fit conversations feel structured and low-friction.",
        content: [
          { type: "Heading", props: { text: "Tell Us About the Work", level: "h1", align: "center" } },
          { type: "Text", props: { content: "Share the problem, timeline, and what success should look like. We’ll tell you quickly whether we’re the right fit and what the next step should be." } },
          { type: "ContactForm", props: { heading: "Project Inquiry", submitLabel: "Send Brief", successMessage: "Thanks. We’ll review the brief and respond shortly." } },
        ],
      },
      ...buildSupplementalPages({
        aboutTitle: "About Northline",
        aboutDescription: "Agency-story starter focused on process, standards, and collaboration style.",
        aboutSummary: "Use an about page that builds trust around the agency’s working model and quality bar.",
        aboutBody: "Northline Creative Agency helps teams move from scattered creative effort to clear, repeatable systems. We combine strategy, design, and execution so the work not only looks strong but also moves key metrics in the right direction.",
        aboutStats: "85+ | Launches delivered\n14 | Specialists across strategy and design\n92% | Repeat engagement rate",
        bookingTitle: "Book a Discovery Call",
        bookingDescription: "Booking starter for agency qualification calls.",
        bookingSummary: "Use a booking page that makes discovery calls feel structured and worth the time.",
        bookingBody: "Use a short discovery call to align on the business problem, timing, internal constraints, and whether a sprint, project, or retainer is the right fit.",
        bookingHeading: "Discovery Call Request",
        bookingCta: "Book Call",
        bookingSuccess: "Request received. We’ll send available times shortly.",
        landingTitle: "Agency Offer Landing",
        landingDescription: "Lead-capture starter for one flagship service or campaign offer.",
        landingSummary: "Use a focused agency landing page for one core offer with one clear qualification path.",
        landingHeroTitle: "Get Strategic Creative Without the Drag",
        landingHeroSubtitle: "Start with a discovery call and see how a tighter creative system can improve launch speed and performance.",
        landingHeroCta: "Start Here",
        landingFeatures: "Strategic Clarity | Know what to prioritize first.\nExecution Depth | Move from ideas into shipped work faster.\nBetter Systems | Build reusable assets, not one-off chaos.",
        landingFormHeading: "Request a Discovery Call",
        landingFormCta: "Apply",
        landingFormSuccess: "Thank you. We’ll review fit and respond with next steps shortly.",
      }),
    ],
  },
];

export function listStarterTemplates(): VerticalStarterTemplate[] {
  return STARTER_TEMPLATES;
}

export function getStarterTemplateByVertical(vertical: string): VerticalStarterTemplate | undefined {
  return STARTER_TEMPLATES.find((template) => template.vertical === vertical);
}

export function getStarterTemplatePage(
  vertical: string,
  pageId?: string,
): VerticalStarterPage | undefined {
  const template = getStarterTemplateByVertical(vertical);
  if (!template) {
    return undefined;
  }

  if (!pageId) {
    return template.pages[0];
  }

  return template.pages.find((page) => page.id === pageId) ?? template.pages[0];
}

export function buildStarterTemplatePrompt(
  template: VerticalStarterTemplate,
  page: VerticalStarterPage,
): string {
  const sectionTypes = summarizeSectionTypes(page.content);

  return [
    `Use the ${template.name} starter as the structural direction for this draft.`,
    template.description,
    `Focus on the ${page.title} starter for a ${page.kind} page.`,
    page.aiSummary,
    sectionTypes ? `Model the first draft around these section types: ${sectionTypes}.` : "",
    "Rewrite all placeholder copy for the actual business instead of reusing starter text verbatim.",
  ]
    .filter(Boolean)
    .join(" ");
}
export * from "./registry.js";
